import {defineStore} from 'pinia'
import Worker from '../workers/pressureSignals?worker'
import AcPowWorker from '../workers/averageAcousticPowerMonteCarlo.js?worker'
import DutyCycleWorker from '../workers/dutyCycle.js?worker'
import {getTIC} from "../workers/common.js"
import {Dialog, Notify} from 'quasar'

import {
    findMostConservativeValue,
    lookupTable,
    ultrasoundBeamDiameterArray,
    ultrasoundFrequencykHZArray
} from "./lookuptable.ts"

export const useCalculatorStore = defineStore('calculator', {
    state: () => ({
        loadingValue: 0,
        showWorkerDialog: false,
        currentWorkerJob: '',
        currentCancelFn: null,
        importingData: false,
        MR_SAR: 0,
        TUS_Geometry_Factor_at_Focus: 1,
        max_temp_rise_us_protocol: 0,
        max_temp_rise_steady_state: 0,
        max_thermal_dose: 0,
        loading_processes: [],
        acoustic_power: 17600,
        average_acoustic_power: 2.93,
        electrical_power: 17600,
        transducer_efficiency: 100,
        updateSeenTrainSection: false,
        canvasRefreshButton: true,
        showTimingGraph: false,
        confirmConditionsOfUse: false,
        isDarkModeGlobal: false,
        isTooltipEnabled: false,
        timingParameters: true,
        isSecondLevelTimingParameters: false,
        isThirdLevelTimingParameters: false,
        plotImage: null,
        ppp_mpa: 1.43,
        pnp_mpa: 1.43,
        isppa: 69.1762,
        ispta_pulse_train: 69.1762,
        ispta_pulse_train_repetition: 69.1762,
        MI: 1.8873,
        derated_ppp_mpa: 0.8138559,
        derated_pnp_mpa: 0.8138559,
        derated_isppa: 20.48,
        derated_ispta_pulse_train: 20.48,
        derated_ispta_pulse_train_repetition: 20.48,
        derated_MI: 1.15,
        non_derated_MI: 2.02,
        frequency_MHz: 0.5,
        derate: true,

        selectedFreeFieldWaterProperties: "Water",
        rho_kg_m3_FF: 998,
        c_m_s_FF: 1481,

        selectedInSituWaterProperties: "Brain",
        rho_kg_m3_IS: 1046,
        c_m_s_IS: 1546.3,

        limits: {
          'MI': 1.9,
          'derated_ISPPA': 190,
          'ISPTA': 0.72
        },
        // PULSE PARAMETERS
        pulse_duration_ms: 5,
        percentage_of_pd: 0,
        percentage_pulse_train_repetition_ramp_width: 0,
        percentage_of_pulse_train_ramp_width: 0,
        pulse_ramp_duration_ms: 0,
        pulse_ramp_shape: "Tukey",
        pulse_repetition_interval_ms: 5,
        pulse_train_duration_ms: 5,
        pulse_train_ramp_duration_ms: 0,
        pulse_train_ramp_shape: "Tukey",
        pulse_train_repetition_interval_ms: 5,
        pulse_train_repetition_duration_ms: 5,
        pulse_train_repetition_ramp_duration_ms: 0,
        pulse_train_repetition_ramp_shape: "Tukey",
        Specific_Heat_J_kg_C: 3600,
        Specific_Heat_J_kg_C_BONE_T: 2274,
        Specific_Heat_J_kg_C_BONE_C: 1313,
        Attenuation_db_cm_MHz: 0.5,
        Attenuation_db_cm_MHz_BRAIN: 0.5,
        Attenuation_db_cm_MHz_SKULL_T: 4.69,
        Attenuation_db_cm_MHz_SKULL_C: 4.08,
        diameter_of_transducer_mm: 64,
        focal_depth_mm: 50,
        thickness_of_skull_mm: 10,
        thickness_of_scalp_mm: 5.8,
        coupling_thickness_mm: 10,
        isManualSkullPressureTransmission: false,
        isManualBrainPressureTransmission: false,
        isManualBrainAttenuationCoeff: false,
        skull_pressure_transmission_manual: 0,
        brain_pressure_transmission_manual: 0.933,
        brain_attenuation_coeff_manual: 0.498,
        isEqualPPPPNP: true,
        pulse_train_repetition_duration_max: 18000000,
        isppaFromPressureSignal: [],
        isppaWorker: null,
        avgAcPowWorker: null,
        dutyCycleWorker: null,
        isppaLoading: {
            pulse: null,
            train: null,
            repetition: null
        },
        absorptionAttenuationCoefficient: 0.6,
        absorptionFraction: 0.85,
        rampShapes: ['Tukey','Linear'],
        ppp_mpa_unformatted: null,
        derated_ppp_mpa_unformatted: null,
        density_of_white_matter_kg_m3 : 1027,
        overallExposureTimeManual: 0.01,
        isManualExposureTime: false,
        calculateNotify: null,
        tempRiseTimeUnit: "seconds",
        coeff_dB_cm_MHz: 0.5,
        duty_cycle_of_pulse: 100,
        duty_cycle_of_pulse_train: 100,
        duty_cycle_of_pulse_train_repetition: 100,
        overall_duty_cycle_of_pulse: 100,
        overall_duty_cycle_of_pulse_train: 100,
        overall_duty_cycle_of_pulse_train_repetition: 100,


    }),
    getters: {
        overallDC: (state) => {
            if (state.timingParameters && state.isSecondLevelTimingParameters && state.isThirdLevelTimingParameters) {
                // PTR is open → overall DC = overall duty cycle of pulse train repetition
                return state.overall_duty_cycle_of_pulse_train_repetition
            } else if (state.timingParameters && state.isSecondLevelTimingParameters) {
                // PT is open, PTR is not → overall DC = overall duty cycle of pulse train
                return state.overall_duty_cycle_of_pulse_train
            } else {
                // Only pulse is open → overall DC = overall duty cycle of pulse
                return state.overall_duty_cycle_of_pulse
            }
        },
        exposureTimeBaseInSec: (state) => {
            if (state.allTimeTogglesOn) {
                return state.pulse_train_repetition_duration_in_s
            } else if (state.pulseAndTrainOnly) {
                return state.pulse_train_duration_in_s
            }
            return state.pulse_duration_in_s
        },
        exposureTimeBaseInMinutes: (state) => state.exposureTimeBaseInSec / 60,
        overallExposureTime: (state) => (state.tempRiseTimeUnit === "seconds" ? state.exposureTimeBaseInSec : state.exposureTimeBaseInMinutes) * 2,
        TUS_Max_SAR_at_Focus: (state) => {
            // Determine energy deposition based on pulse type
            let val = state.derated_ispta_pulse_train_repetition
            if (state.pulseOnly) {
                val = state.derated_isppa
            } else if (state.pulseAndTrainOnly) {
                val = state.derated_ispta_pulse_train
            }
            // Attenuation coefficient used to calculate the TUS peak sar should be the absorption attenuation coefficient:
            const numerator = 2 * val * (+state.att_coef_abs * state.frequency_MHz) / 8.686;
            return (numerator / state.density_of_white_matter_kg_m3) * 1000000
        },
        SAR_for_Temperature_Calculation: (state) => +state.TUS_Max_SAR_at_Focus + +state.MR_SAR,
        TUS_Geometry_Factor_at_Focus_in_Range: (state) => {
            if (+state.TUS_Geometry_Factor_at_Focus < 0) return 0
            if (+state.TUS_Geometry_Factor_at_Focus > 1) return 1
            return +state.TUS_Geometry_Factor_at_Focus
        },
        TUS_SAR_at_Focus_averaged: (state) => +state.TUS_Geometry_Factor_at_Focus_in_Range * +state.TUS_Max_SAR_at_Focus,
        totalSARAveragedOver10gmOr10ccWkg: (state) => +(+state.MR_SAR + state.TUS_SAR_at_Focus_averaged).toFixed(4),
        getTIC: (state) => getTIC(state.average_acoustic_power, state.us_beam_diameter_on_skull_cm),
        pulseOnly: (state) => state.timingParameters && !state.isSecondLevelTimingParameters,
        pulseAndTrainOnly: (state) => state.timingParameters && state.isSecondLevelTimingParameters && !state.isThirdLevelTimingParameters,
        allTimeTogglesOn:(state) => state.timingParameters && state.isSecondLevelTimingParameters && state.isThirdLevelTimingParameters,
        //att_coef_abs value = attenuation coefficient x absorption fraction. Attenuation coefficient = 0.6 and absorption fraction = 0.85
        att_coef_abs: (state) => +(state.absorptionAttenuationCoefficient * state.absorptionFraction),
        primary_parameters_valid: (state) => {
            console.log([
                state.ppp_mpa
                , state.isppa
                , state.MI
                , state.frequency_MHz
                , state.rho_kg_m3_FF
                , state.c_m_s_FF
                , state.rho_kg_m3_IS
                , state.c_m_s_IS
                , state.diameter_of_transducer_mm
                , state.focal_depth_mm
                , state.thickness_of_skull_mm
                , state.thickness_of_scalp_mm
                , state.coupling_thickness_mm
                , state.us_beam_diameter_on_skin_mm
                , state.skull_pressure_transmission
                , state.brain_pressure_transmission
                , (state.attenuation_coefficient)
                , state.absorptionFraction
                , state.absorptionAttenuationCoefficient
            ])
            return (
                state.ppp_mpa > 0
                && state.isppa > 0
                && state.MI > 0
                && state.frequency_MHz > 0
                && state.rho_kg_m3_FF > 0
                && state.c_m_s_FF > 0
                && state.rho_kg_m3_IS > 0
                && state.c_m_s_IS > 0
                && state.diameter_of_transducer_mm > 0
                && state.focal_depth_mm > 0
                && state.thickness_of_skull_mm > 0
                && state.thickness_of_scalp_mm > 0
                && state.coupling_thickness_mm > 0
                && state.us_beam_diameter_on_skin_mm > 0
                && state.skull_pressure_transmission > 0
                && state.brain_pressure_transmission > 0
                && (state.attenuation_coefficient) > 0
                && state.absorptionFraction > 0
                && state.absorptionAttenuationCoefficient > 0
            )
        },
        time_parameters_gt_0: (state) => {
            return (state.pulse_duration_ms > 0
                // && state.percentage_of_pri > 0
                && state.percentage_of_pd >= 0
                && state.percentage_of_pulse_train_ramp_width >= 0
                // && state.percentage_of_ptri > 0
                && state.percentage_pulse_train_repetition_ramp_width >= 0
                && state.pulse_ramp_duration_ms >= 0
                && state.pulse_repetition_interval_ms > 0
                && state.pulse_train_duration_ms  > 0
                && state.pulse_train_ramp_duration_ms >= 0
                && state.pulse_train_repetition_interval_ms > 0
                && state.pulse_train_repetition_duration_ms > 0
                && state.pulse_train_repetition_ramp_duration_ms >= 0
          )
        },
        time_parameters_valid: (state) => {
            // None of the timing properties can be negative
            if (+state.pulse_duration_ms <= 0) return false
            if (+state.pulse_repetition_interval_ms <= 0) return false
            if (+state.pulse_train_duration_ms <= 0) return false
            if (+state.pulse_train_repetition_interval_ms <= 0) return false
            if (+state.pulse_train_repetition_duration_ms <= 0) return false
            if (+state.pulse_ramp_duration_ms < 0) return false
            if (+state.pulse_train_ramp_duration_ms < 0) return false
            if (+state.pulse_train_repetition_ramp_duration_ms < 0) return false
            if (+state.percentage_of_pd < 0) return false
            if (+state.percentage_of_pulse_train_ramp_width < 0) return false
            if (+state.percentage_pulse_train_repetition_ramp_width < 0) return false

            const ramp_duration_limit = +state.pulse_duration_ms / 2
            const pulse_train_ramp_duration_limit = (+state.pulse_train_duration_ms / 2) - (Math.floor(state.pulse_repetition_interval_ms - state.pulse_duration_ms) / 2)
            const pulse_train_repetition_ramp_duration_limit = (+state.pulse_train_repetition_duration_ms - Math.floor(state.pulse_repetition_interval_ms - state.pulse_duration_ms)) / 2
            return (!((+state.pulse_ramp_duration_ms > ramp_duration_limit)
                // The repetition interval should always be larger than the pulse duration.
                || (state.pulse_repetition_interval_ms < +state.pulse_duration_ms)
                // Don't allow a Pulse Train Repetition Interval smaller than Pulse Train Duration (equal is allowed).
                || (+state.pulse_train_repetition_interval_ms < +state.pulse_train_duration_ms)
                // Don't allow a Pulse Train Duration smaller than the Pulse Repetition Interval (equal is allowed).
                // || (+state.pulse_train_duration_ms < +state.pulse_repetition_interval_ms)
                // Don't allow a Pulse Train Repetition Duration smaller than the Pulse Train Repetition Interval (equal is allowed).
                || (+state.pulse_train_repetition_duration_ms < +state.pulse_train_repetition_interval_ms)
                || (+state.pulse_train_repetition_duration_ms > state.pulse_train_repetition_duration_max)
                // Check validity pulse_train_ramp_duration_ms
                || (+state.pulse_train_ramp_duration_ms > pulse_train_ramp_duration_limit
                // Check validity pulse_train_repetition_ramp_duration_ms
                || +state.pulse_train_repetition_ramp_duration_ms > pulse_train_repetition_ramp_duration_limit))
            )
        },
        us_beam_diameter_on_skin_mm: (state) => {
            // US beam diameter on skin [mm] -> calculated in the following way
            // Equations used in the background:
            // Radius = Diameter of transducer / 2
            // angle [degrees] = arctan(Radius / Focal depth)
            // radius decrease = tan(angle) * Distance between exit plan and skin
            // US beam diameter on skin = Diameter of transducer - (radius decrease * 2)`
            const radius = +state.diameter_of_transducer_mm / 2
            const angle = Math.atan(radius / +state.focal_depth_mm)
            const radius_decrease = Math.tan(angle) * +state.coupling_thickness_mm
            return +(state.diameter_of_transducer_mm - +(radius_decrease * 2)).toFixed(1)
        },
        us_beam_diameter_on_skull_mm: (state) => {
            // US beam diameter on skull [Mm] -> calculated in the following way
            // Equations used in the background:
            // Radius = Diameter of transducer / 2
            // angle [degrees] = arctan(Radius / Focal depth)
            // radius decrease = tan(angle) * Distance between exit plan and skin
            // US beam diameter on skull = Diameter of transducer - (radius decrease * 2)`
            const radius = state.diameter_of_transducer_mm / 2
            const angle = Math.atan(radius / state.focal_depth_mm)
            const radius_decrease = Math.tan(angle) * (+state.coupling_thickness_mm + +state.thickness_of_scalp_mm)
            return +(state.diameter_of_transducer_mm - (radius_decrease * 2)).toFixed(1)
        },
        us_beam_diameter_on_skull_cm: (state) => +(state.us_beam_diameter_on_skull_mm  / 10).toFixed(2),
        distance_in_brain_cm: (state) => (+state.focal_depth_mm - +state.thickness_of_skull_mm - +state.thickness_of_scalp_mm - +state.coupling_thickness_mm) / 10,
        // coeff_dB_cm_MHz: (state) => ((0.5 * (state.derate == 1)) + (0 * (state.derate == 0))), // aka alpha_brain
        getPressureTransmission: (state) => {
            const conservativeFreq = findMostConservativeValue(state.frequency_MHz * 1000, ultrasoundFrequencykHZArray);
            const conservativeDiameter = findMostConservativeValue(state.us_beam_diameter_on_skull_mm, ultrasoundBeamDiameterArray);
            const mostConservativeValue = lookupTable[`${conservativeFreq},${conservativeDiameter}`];
            return (mostConservativeValue / 100)
        },
        d_reas_coeff: (state) => state.isManualSkullPressureTransmission ? state.skull_pressure_transmission_manual : state.getPressureTransmission,
        energy_deposition: (state) => state.calculate_energy_deposition('freefield'), // energy deposition
        derated_energy_deposition: (state) => state.calculate_energy_deposition('derated'),
        pulse_duration_in_s: (state) => state.pulse_duration_ms / 1000,
        pulse_train_duration_in_s: (state) => state.pulse_train_duration_ms / 1000,
        pulse_train_repetition_duration_in_s: (state) => state.pulse_train_repetition_duration_ms / 1000,
        rate_of_energy_deposition: (state) => {
            if (state.pulseOnly) {
                return +(state.energy_deposition / state.pulse_duration_in_s).toFixed(3)
            } else if (state.pulseAndTrainOnly) {
                return +(state.energy_deposition / state.pulse_train_duration_in_s).toFixed(3)
            } else {
                return +(state.energy_deposition / state.pulse_train_repetition_duration_in_s).toFixed(3)
            }
        },
        derated_rate_of_energy_deposition: (state) => {
            if (state.pulseOnly) {
                return +(state.derated_energy_deposition / state.pulse_duration_in_s).toFixed(3)
            } else if (state.pulseAndTrainOnly) {
                return +(state.derated_energy_deposition / state.pulse_train_duration_in_s).toFixed(3)
            } else {
                return +(state.derated_energy_deposition / state.pulse_train_repetition_duration_in_s).toFixed(3)
            }
        },
        attenuation_coefficient(state) {
            if (state.isManualBrainAttenuationCoeff) {
              return +state.brain_attenuation_coeff_manual
            }

            // Use the manual pressure transmission value only, not the full getter
            const transmission = state.isManualBrainPressureTransmission
              ? +state.brain_pressure_transmission_manual
              : undefined

            const referenceTransmission = transmission || 0.933; // fallback default

            const newCoeff = -(
              Math.log(referenceTransmission) * 8.686
            ) / (state.distance_in_brain_cm * state.frequency_MHz)

            return +newCoeff.toFixed(3)
          },

          brain_pressure_transmission(state) {
            if (state.isManualBrainPressureTransmission) {
              return +state.brain_pressure_transmission_manual
            }

            // Use the coefficient directly from state, not from the getter
            const coeff = state.isManualBrainAttenuationCoeff
              ? state.brain_attenuation_coeff_manual
              : state.coeff_dB_cm_MHz

            return +Math.exp(
                -state.distance_in_brain_cm * state.frequency_MHz * coeff / 8.686
            ).toFixed(3)
          },
        z_kg_s_m2_FF: (state) =>  state.rho_kg_m3_FF * state.c_m_s_FF,
        z_kg_s_m2_IS: (state) =>  state.rho_kg_m3_IS * state.c_m_s_IS,
        z_Rayl_FF: (state) =>  state.z_kg_s_m2_FF / 1000000,
        z_Rayl_IS: (state) =>  state.z_kg_s_m2_IS / 1000000,

        P_Ave_norm: (state) => (state.pulse_duration_ms - state.pulse_ramp_duration_ms) / state.pulse_duration_ms,
        DC_pulse_train: (state) => state.pulse_duration_ms / state.pulse_repetition_interval_ms,
        DC_pulse_train_repetition: (state) => state.pulse_train_duration_ms / state.pulse_train_repetition_interval_ms,
        skull_pressure_transmission: (state) => state.d_reas_coeff,
        integral_duration_s: (state) => +state.pulse_duration_ms / 1000 * +state.pulse_train_duration_ms / +state.pulse_repetition_interval_ms,
        Density_kg_cm3_FF: (state) => state.rho_kg_m3_FF / 10 ** 6,
        Density_kg_cm3_IS: (state) => state.rho_kg_m3_IS / 10 ** 6,
        two_alphaI_rhoCt: (state) => state.calculate_two_alphaI_rhoCt(
            (state.att_coef_abs * state.frequency_MHz) / 8.686,
            state.isppa,
            state.Density_kg_cm3_FF,
            state.Specific_Heat_J_kg_C
        ),
        two_alphaI_rhoCt_derated: (state) => state.calculate_two_alphaI_rhoCt(
            (state.att_coef_abs * state.frequency_MHz) / 8.686,
            state.derated_isppa,
            state.Density_kg_cm3_IS,
            state.Specific_Heat_J_kg_C
        ),
        Temp_Rise_C: state => state.two_alphaI_rhoCt * state.integral_duration_s,
        Temp_Rise_C_derated: state => (state.two_alphaI_rhoCt_derated * state.integral_duration_s),
        depth_cm: (state) => (state.focal_depth_mm - +state.coupling_thickness_mm) / 10,
        depth_mm: (state) => state.focal_depth_mm - +state.coupling_thickness_mm
    },
    actions: {
        activateCanvasRefreshButton() {
          this.canvasRefreshButton = true
        },
        switchToManualSkullPressureTransmission (bool) {
            this.skull_pressure_transmission_manual = this.skull_pressure_transmission
            this.isManualSkullPressureTransmission = bool
            if (!bool) {
                this.updateByPr()
            }
        },
        showDependentParameterDialog() {
            const message = `The pressure transmission and the attenuation coefficient are dependent on each other. To modify this value, first restore the other field.`
            Dialog.create({
                title: 'Brain pressure transmission and brain attenuation coefficient dependency',
                message: message,
                persistent: true,
                html: false,
                color: 'primary',
                dark: this.isDarkModeGlobal
            })
        },
        switchToManualBrainPressureTransmission(bool) {
          // Always update manual field with current calculated value
          this.brain_pressure_transmission_manual = this.brain_pressure_transmission;

          if (this.isManualBrainAttenuationCoeff && bool) {
            this.showDependentParameterDialog(); // warn user
            return;
          }

          this.isManualBrainPressureTransmission = bool;

          // When turning off manual mode, update attenuation based on PR
          if (!bool) {
            this.updateByPr();
          }
        },
        // Called when user tries to edit attenuation coefficient manually
        switchToManualBrainAttenuationCoeff(bool) {
          // Initialize to default when switching to manual
          this.brain_attenuation_coeff_manual = 0.498;

          if (this.isManualBrainPressureTransmission && bool) {
            this.showDependentParameterDialog(); // warn user
            return;
          }

          this.isManualBrainAttenuationCoeff = bool;

          // When turning off manual mode, update attenuation from PR
          if (!bool) {
            this.updateByPr();
          }
        },
        calculate_two_alphaI_rhoCt (attenuation_np_cm, isppa, density, specific_heat) {
            return 2 * attenuation_np_cm * isppa / density / specific_heat
        },
        calculate_energy_deposition(table) {
            let energy_deposition

            // Define variables based on the table value
            let isppa = table === 'freefield' ? this.isppa : this.derated_isppa
            let ispta_pulse_train = table === 'freefield' ? this.ispta_pulse_train : this.derated_ispta_pulse_train
            let ispta_pulse_train_repetition = table === 'freefield' ? this.ispta_pulse_train_repetition : this.derated_ispta_pulse_train_repetition

            // Determine energy deposition based on pulse type
            if (this.pulseOnly) {
                energy_deposition = this.round(isppa * this.pulse_duration_ms)
            } else if (this.pulseAndTrainOnly) {
                energy_deposition = this.round(ispta_pulse_train * this.pulse_train_duration_ms)
            } else {
                energy_deposition = this.round(ispta_pulse_train_repetition * this.pulse_train_repetition_duration_ms)
            }

            return +(energy_deposition * (10 ** -3)).toFixed(3);
        },
        generatePrMIData() {
          const data = []
          const max_freq = this.frequency_MHz <= 3 ? 3 : +this.frequency_MHz * 2
          const step = this.frequency_MHz <= 3 ? 0.05 : 0.1
          for (let freq = 0; freq <= max_freq; freq += step) {
            data.push([freq, +(this.limits.MI * Math.sqrt(freq)).toFixed(2)]);
          }
          return data
        },
        getMyPCTDMIOSTACFValue() {
          const attenuation_coefficient = 0.3
          const attenuation_factor =  attenuation_coefficient * this.depth_cm * this.frequency_MHz
          const attenuated_part = Math.exp( -attenuation_factor / 8.686)
          const soft_attenuated_pr = +(this.ppp_mpa * attenuated_part).toFixed(4)
          return [[ this.frequency_MHz, soft_attenuated_pr ]]
        },
        getMyValue() {
          return this.derate ? [[ this.frequency_MHz, this.derated_ppp_mpa ]] : null
        },
        getMyNotDeratedValue() {
          return [[ this.frequency_MHz, this.ppp_mpa ]]
        },
        round(val) {
            return Number.parseFloat(Number.parseFloat(val).toFixed(2))
        },
        restartWorker() {
          if (this.isppaWorker) {
              this.isppaWorker.destroy()
          }
          if (this.dutyCycleWorker) {
              this.dutyCycleWorker.destroy()
          }
          if (this.avgAcPowWorker) {
              this.avgAcPowWorker.destroy()
          }
        },
        showRecalculateButton() {
            if (this.time_parameters_valid) {
                this.activateCanvasRefreshButton()

            this.calculateNotify = Notify.create({
                timeout: 0,
                message: 'Timing parameters changed',
                badgeStyle: 'display: none',
                color: 'teal-4',
                textColor: 'white',
                position: 'bottom',
                caption: 'Click the button to apply',
                actions: [
                    {
                        label: 'Calculate',
                        title: 'Calculate',
                        size: "md",
                        icon: 'calculate',
                        color: 'primary',
                        round: false,
                        flat: false,
                        textColor: 'white',
                        handler: this.recalculate
                    }
                ]
            })

            } else {
                this.clearNotifications()
            }
        },
        clearNotifications() {
            if (this.calculateNotify) {
                this.calculateNotify()
            }
        },
        recalculate() {
            console.log('recalculate')
            console.info(this.primary_parameters_valid)
            console.info(this.time_parameters_valid)
            if (this.primary_parameters_valid && this.time_parameters_valid) {
              console.log('parameters valid')

              this.updateByPr()
              this.getAverageAcousticPower()
              this.getDutyCycle()
          }
          this.clearNotifications()
        },
        initAcousticPowerWorker() {
            if (!this.avgAcPowWorker) {
                this.avgAcPowWorker = new AcPowWorker({type: 'module'})
                this.avgAcPowWorker.onmessage = (evt) => {
                    this.average_acoustic_power = evt.data.average_acoustic_power
                    this.showWorkerDialog = false
                    this.loading_processes.pop('acousticPower')
                }
            }
        },
        initDutyCycleWorker() {
            if (!this.dutyCycleWorker) {
                this.dutyCycleWorker = new DutyCycleWorker({type: 'module'})
                this.dutyCycleWorker.onmessage = (evt) => {
                    this.duty_cycle_of_pulse = evt.data.duty_cycle_of_pulse
                    this.duty_cycle_of_pulse_train = evt.data.duty_cycle_of_pulse_train
                    this.duty_cycle_of_pulse_train_repetition = evt.data.duty_cycle_of_pulse_train_repetition
                    this.overall_duty_cycle_of_pulse = evt.data.overall_duty_cycle_of_pulse
                    this.overall_duty_cycle_of_pulse_train = evt.data.overall_duty_cycle_of_pulse_train
                    this.overall_duty_cycle_of_pulse_train_repetition = evt.data.overall_duty_cycle_of_pulse_train_repetition
                    this.showWorkerDialog = false
                    this.loading_processes.pop('dutyCycle')
                }
            }
        },
        initIsppaWorker() {
            if (typeof Worker !== "undefined") {
                if (!this.isppaWorker) {
                    this.isppaWorker = new Worker({type: 'module'})
                    this.isppaWorker.onmessage = (evt) => {
                        if (evt.data.job === 'pampl-result') {
                            this.ppp_mpa_unformatted = evt.data.ppp_mpa
                            this.derated_ppp_mpa_unformatted = evt.data.derated_ppp_mpa
                            this.ppp_mpa = +(this.ppp_mpa_unformatted.toFixed(4))
                            this.pnp_mpa = this.ppp_mpa
                            this.derated_ppp_mpa = (this.derated_ppp_mpa_unformatted.toFixed(4))
                            this.derated_pnp_mpa = this.derated_ppp_mpa
                            this.derated_isppa = evt.data.derated_isppa

                            if (this.pulseAndTrainOnly || this.allTimeTogglesOn) {
                                this.ispta_pulse_train = evt.data.ispta_pulse_train
                                this.derated_ispta_pulse_train = evt.data.derated_ispta_pulse_train
                            } else {
                                this.ispta_pulse_train = 0
                                this.derated_ispta_pulse_train = 0
                            }

                            if (this.allTimeTogglesOn) {
                                this.ispta_pulse_train_repetition = evt.data.ispta_pulse_train_repetition
                                this.derated_ispta_pulse_train_repetition = evt.data.derated_ispta_pulse_train_repetition
                            } else {
                                this.ispta_pulse_train_repetition = 0
                                this.derated_ispta_pulse_train_repetition = 0
                            }

                            this.showWorkerDialog = false
                            this.loading_processes.pop('pampl')

                            console.info("Worker returned pampl-result")
                        }

                        if (evt.data.job === 'isppa') {
                            this.isppa = +(evt.data.isppa.toFixed(4))
                            this.derated_isppa = evt.data.derated_isppa
                            if (this.pulseAndTrainOnly || this.allTimeTogglesOn) {
                                this.ispta_pulse_train = evt.data.ispta_pulse_train
                                this.derated_ispta_pulse_train = evt.data.derated_ispta_pulse_train
                            } else {
                                this.ispta_pulse_train = 0
                                this.derated_ispta_pulse_train = 0
                            }

                            if (this.allTimeTogglesOn) {
                                this.ispta_pulse_train_repetition = evt.data.ispta_pulse_train_repetition
                                this.derated_ispta_pulse_train_repetition = evt.data.derated_ispta_pulse_train_repetition
                            } else {
                                this.ispta_pulse_train_repetition = 0
                                this.derated_ispta_pulse_train_repetition = 0
                            }
                            this.showWorkerDialog = false
                            this.loading_processes.pop('isppa')

                            console.info("Worker returned isppa")
                        }
                        this.calculateMI(evt.data.trigger)
                    }
                }
            }
        },
        initWorkers() {
            this.initAcousticPowerWorker()
            this.initDutyCycleWorker()
            this.initIsppaWorker()
        },
        getAverageAcousticPower(init = false){
            console.log("getAverageAcousticPower")
            if (typeof AcPowWorker !== "undefined") {
                let message = {
                    acoustic_power: this.acoustic_power,
                    pulseOnly: this.pulseOnly,
                    pulseAndTrainOnly: this.pulseAndTrainOnly,
                    allTimeTogglesOn: this.allTimeTogglesOn,
                    job: 'average_acoustic_power',
                    isppa: +this.isppa,
                    derated_isppa: this.derated_isppa,
                    z_Rayl_IS: this.z_Rayl_IS,
                    z_Rayl_FF: this.z_Rayl_FF,
                    pulseDur: this.pulse_duration_ms,
                    rampDur: this.pulse_ramp_duration_ms,
                    d_reas_coeff: this.d_reas_coeff,
                    brain_pressure_transmission: this.isManualBrainPressureTransmission ? this.brain_pressure_transmission_manual : this.brain_pressure_transmission,
                    pulseRepetitionInterval: this.pulse_repetition_interval_ms,
                    pulseTrainDuration: this.pulse_train_duration_ms,
                    pulseTrainRampDuration: this.pulse_train_ramp_duration_ms,
                    pulseTrainRepetitionInterval: this.pulse_train_repetition_interval_ms,
                    nPulses: this.pulse_train_duration_ms / this.pulse_repetition_interval_ms,
                    nTrains: this.pulse_train_repetition_duration_ms / this.pulse_train_repetition_interval_ms,
                    time_parameters_valid: this.time_parameters_valid,
                    pulse_ramp_shape: this.pulse_ramp_shape,
                    pulse_ramp_duration_ms: this.pulse_ramp_duration_ms,
                    showPulseTrain: this.isSecondLevelTimingParameters,
                    showPulseTrainRepetition: this.isThirdLevelTimingParameters,
                    ppp_mpa: this.ppp_mpa_unformatted ? this.ppp_mpa_unformatted : this.ppp_mpa,
                    derated_ppp_mpa: this.derated_ppp_mpa_unformatted ? this.derated_ppp_mpa_unformatted : this.derated_ppp_mpa,
                    frequency_MHz: this.frequency_MHz,
                    pulseTrainRepetitionDuration: this.pulse_train_repetition_duration_ms,
                    pulseTrainRampShape: this.pulse_train_ramp_shape,
                    pulseTrainRepetitionRampShape: this.pulse_train_repetition_ramp_shape,
                    pulseTrainRepetitionRampDuration: this.pulse_train_repetition_ramp_duration_ms,
                }

                message = this.timeParametersByToggles(message)
                this.loading_processes.push('acousticPower')

                if (!init) {
                    this.showLoadingWithCancel('average_acoustic_power', () => {
                        this.loading_processes.pop('acousticPower')
                        this.avgAcPowWorker?.terminate()
                        this.avgAcPowWorker = null
                        console.info('Worker cancelled by user')
                        this.initWorkers()
                    })
                }
                this.avgAcPowWorker.postMessage(message);
            } else {
              // Fallback mechanism for browsers without web worker support
              console.error('Your browser does not support web workers')
            }
        },
        getPampl() {
            if (typeof Worker !== "undefined") {
                let message = {
                    pulseOnly: this.pulseOnly,
                    pulseAndTrainOnly: this.pulseAndTrainOnly,
                    allTimeTogglesOn: this.allTimeTogglesOn,
                    job: 'pampl',
                    isppa: +this.isppa,
                    derated_isppa: this.derated_isppa,
                    z_Rayl_IS: this.z_Rayl_IS,
                    z_Rayl_FF: this.z_Rayl_FF,
                    pulseDur: this.pulse_duration_ms,
                    rampDur: this.pulse_ramp_duration_ms,
                    d_reas_coeff: this.d_reas_coeff,
                    brain_pressure_transmission: this.isManualBrainPressureTransmission ? this.brain_pressure_transmission_manual : this.brain_pressure_transmission,
                    pulseRepetitionInterval: this.pulse_repetition_interval_ms,
                    pulseTrainDuration: this.pulse_train_duration_ms,
                    pulseTrainRampDuration: this.pulse_train_ramp_duration_ms,
                    pulseTrainRepetitionInterval: this.pulse_train_repetition_interval_ms,
                    nPulses: this.pulse_train_duration_ms / this.pulse_repetition_interval_ms,
                    nTrains: this.pulse_train_repetition_duration_ms / this.pulse_train_repetition_interval_ms,
                    time_parameters_valid: this.time_parameters_valid,
                    pulse_ramp_shape: this.pulse_ramp_shape,
                    pulse_ramp_duration_ms: this.pulse_ramp_duration_ms,
                    showPulseTrain: this.isSecondLevelTimingParameters,
                    showPulseTrainRepetition: this.isThirdLevelTimingParameters,
                    ppp_mpa: this.ppp_mpa_unformatted ? this.ppp_mpa_unformatted : this.ppp_mpa,
                    derated_ppp_mpa: this.derated_ppp_mpa_unformatted ? this.derated_ppp_mpa_unformatted : this.derated_ppp_mpa,
                    frequency_MHz: this.frequency_MHz,
                    pulseTrainRepetitionDuration: this.pulse_train_repetition_duration_ms,
                    pulseTrainRampShape: this.pulse_train_ramp_shape,
                    pulseTrainRepetitionRampShape: this.pulse_train_repetition_ramp_shape,
                    pulseTrainRepetitionRampDuration: this.pulse_train_repetition_ramp_duration_ms,
                }

                message = this.timeParametersByToggles(message)
                this.loading_processes.push('pampl')

                this.showLoadingWithCancel('pampl', () => {
                  this.loading_processes.pop('pampl')
                  this.isppaWorker?.terminate()
                  this.isppaWorker = null
                  console.info('Worker cancelled by user')
                  this.initWorkers()
                })
                this.isppaWorker.postMessage(message);
            } else {
              // Fallback mechanism for browsers without web worker support
              console.error('Your browser does not support web workers')
            }
        },
        getDutyCycle() {
            if (typeof Worker !== "undefined") {
                let message = {
                    pulseOnly: this.pulseOnly,
                    pulseAndTrainOnly: this.pulseAndTrainOnly,
                    allTimeTogglesOn: this.allTimeTogglesOn,
                    job: 'dutyCycle',
                    isppa: +this.isppa,
                    derated_isppa: this.derated_isppa,
                    z_Rayl_IS: this.z_Rayl_IS,
                    z_Rayl_FF: this.z_Rayl_FF,
                    pulseDur: this.pulse_duration_ms,
                    rampDur: this.pulse_ramp_duration_ms,
                    d_reas_coeff: this.d_reas_coeff,
                    brain_pressure_transmission: this.isManualBrainPressureTransmission ? this.brain_pressure_transmission_manual : this.brain_pressure_transmission,
                    pulseRepetitionInterval: this.pulse_repetition_interval_ms,
                    pulseTrainDuration: this.pulse_train_duration_ms,
                    pulseTrainRampDuration: this.pulse_train_ramp_duration_ms,
                    pulseTrainRepetitionInterval: this.pulse_train_repetition_interval_ms,
                    nPulses: this.pulse_train_duration_ms / this.pulse_repetition_interval_ms,
                    nTrains: this.pulse_train_repetition_duration_ms / this.pulse_train_repetition_interval_ms,
                    time_parameters_valid: this.time_parameters_valid,
                    pulse_ramp_shape: this.pulse_ramp_shape,
                    pulse_ramp_duration_ms: this.pulse_ramp_duration_ms,
                    showPulseTrain: this.isSecondLevelTimingParameters,
                    showPulseTrainRepetition: this.isThirdLevelTimingParameters,
                    ppp_mpa: this.ppp_mpa_unformatted ? this.ppp_mpa_unformatted : this.ppp_mpa,
                    derated_ppp_mpa: this.derated_ppp_mpa_unformatted ? this.derated_ppp_mpa_unformatted : this.derated_ppp_mpa,
                    frequency_MHz: this.frequency_MHz,
                    pulseTrainRepetitionDuration: this.pulse_train_repetition_duration_ms,
                    pulseTrainRampShape: this.pulse_train_ramp_shape,
                    pulseTrainRepetitionRampShape: this.pulse_train_repetition_ramp_shape,
                    pulseTrainRepetitionRampDuration: this.pulse_train_repetition_ramp_duration_ms,
                }

                message = this.timeParametersByToggles(message)
                this.loading_processes.push('dutyCycle')

                this.showLoadingWithCancel('dutyCycle', () => {
                  this.loading_processes.pop('dutyCycle')
                  this.dutyCycleWorker?.terminate()
                  this.dutyCycleWorker = null
                  console.info('Worker cancelled by user')
                  this.initWorkers()
                })

                this.dutyCycleWorker.postMessage(message);
            } else {
              // Fallback mechanism for browsers without web worker support
              console.error('Your browser does not support web workers')
            }

        },
        async showLoadingWithCancel(jobName, cancelFn) {
            this.currentWorkerJob = jobName
            this.currentCancelFn = () => {
                cancelFn()
                this.showWorkerDialog = false
                this.showRecalculateButton()
            }
            this.showWorkerDialog = true
        },
        timeParametersByToggles(message) {
            if (this.time_parameters_valid) {
                    // Only pulse toggle on:
                    // Pulse Repetition Interval, Pulse Train Duration, Pulse Train Repetition Interval, Pulse train repetition duration equal the pulse duration.
                    // Pulse Train Ramp duration and Pulse Train Repetition Ramp duration are zero.
                    // Hide both Ispta's from results (non-derated and derated) and don't do the calculations to save time.
                    if (this.pulseOnly) {
                        message.pulseRepetitionInterval = this.pulse_duration_ms
                        message.pulseTrainDuration = this.pulse_duration_ms
                        message.pulseTrainRepetitionInterval = this.pulse_duration_ms
                        message.pulseTrainDuration = this.pulse_duration_ms
                        message.pulseTrainRampDuration = 0
                        message.pulseTrainRepetitionRampDuration = 0
                        message.label = "Isppa"
                    }

                    else if (this.pulseAndTrainOnly) {
                        // Only pulse and pulse train toggle on:
                        // Pulse Train Repetition Interval, Pulse train repetition duration equal Pulse Train Duration.
                        //  Pulse Train Repetition Ramp duration is zero.
                        // Hide Ispta of pulse train repetition from results (non-derated and derated) and don't do the calculation to save time. Potentially add warning that calculation may take a time.
                        message.pulseTrainRepetitionInterval = this.pulse_train_duration_ms
                        message.pulseTrainRepetitionDuration = this.pulse_train_duration_ms
                        message.pulseTrainRepetitionRampDuration = 0
                        message.label = "Isppa and Ispta of pulse train"
                    } else {
                        message.label = "Isppa, Ispta of pulse train and Ispta of pulse train repetition"
                    }
                }
            return message
        },
        getIsppa(ppp_mpa, derated_ppp_mpa, job, trigger) {
            if (typeof Worker !== "undefined") {
                let message = {
                    job: job,
                    trigger: trigger,
                    pulseOnly: this.pulseOnly,
                    pulseAndTrainOnly: this.pulseAndTrainOnly,
                    allTimeTogglesOn: this.allTimeTogglesOn,
                    isppa: +this.isppa,
                    derated_isppa: this.derated_isppa,
                    z_Rayl_IS: this.z_Rayl_IS,
                    z_Rayl_FF: this.z_Rayl_FF,
                    pulseDur: this.pulse_duration_ms,
                    rampDur: this.pulse_ramp_duration_ms,
                    d_reas_coeff: this.d_reas_coeff,
                    brain_pressure_transmission: this.isManualBrainPressureTransmission ? this.brain_pressure_transmission_manual : this.brain_pressure_transmission,
                    pulseRepetitionInterval: this.pulse_repetition_interval_ms,
                    pulseTrainDuration: this.pulse_train_duration_ms,
                    pulseTrainRampDuration: this.pulse_train_ramp_duration_ms,
                    pulseTrainRepetitionInterval: this.pulse_train_repetition_interval_ms,
                    nPulses: this.pulse_train_duration_ms / this.pulse_repetition_interval_ms,
                    nTrains: this.pulse_train_repetition_duration_ms / this.pulse_train_repetition_interval_ms,
                    time_parameters_valid: this.time_parameters_valid,
                    pulse_ramp_shape: this.pulse_ramp_shape,
                    pulse_ramp_duration_ms: this.pulse_ramp_duration_ms,
                    showPulseTrain: this.isSecondLevelTimingParameters,
                    showPulseTrainRepetition: this.isThirdLevelTimingParameters,
                    ppp_mpa: ppp_mpa,
                    derated_ppp_mpa: derated_ppp_mpa,
                    frequency_MHz: this.frequency_MHz,
                    pulseTrainRepetitionDuration: +this.pulse_train_repetition_duration_ms,
                    pulseTrainRampShape: this.pulse_train_ramp_shape,
                    pulseTrainRepetitionRampShape: this.pulse_train_repetition_ramp_shape,
                    pulseTrainRepetitionRampDuration: this.pulse_train_repetition_ramp_duration_ms,
                }

                message = this.timeParametersByToggles(message)

                this.loading_processes.push('isppa')

                // show overlay before posting job
                this.showLoadingWithCancel('isppa', () => {
                  this.loading_processes.pop('isppa')
                  this.isppaWorker?.terminate()
                  this.isppaWorker = null
                  this.initWorkers()
                })

                this.isppaWorker.postMessage(message);
            } else {
              // Fallback mechanism for browsers without web worker support
              console.error('Your browser does not support web workers')
            }
        },
        calculateMI(trigger) {
            //  Mechanical index, standardised indicator related to the potential for mechanical bioeffects, specifically cavitation.
            //  The peak-rarefactional pressure derated using an attenuation coefficient of 0.3 dB/cm/MHz is used in this expression,
            //  as this is the formal definition of MI.
            //  Please see the ITRUSST Standardized Reporting Consensus for further explanation.
            const attenuation_coefficient = 0.3

            const attenuation_factor =  attenuation_coefficient * this.depth_cm * this.frequency_MHz
            const peak_rarefractional_pressure = this.pnp_mpa * (Math.exp( -attenuation_factor / 8.686))

            if (trigger !== 'MI') {
                this.MI = +((peak_rarefractional_pressure / Math.sqrt(this.frequency_MHz)).toFixed(4))
            }

            if (trigger !== 'non_derated_MI') {
                this.non_derated_MI = +(this.pnp_mpa / Math.sqrt(this.frequency_MHz)).toFixed(4)
            }

            this.derated_MI = this.derated_pnp_mpa / Math.sqrt(this.frequency_MHz)
        },
        updateByPr() {
            console.log('updateByPr')
            if (!this.primary_parameters_valid && !this.time_parameters_valid) return
            this.ppp_mpa_unformatted = this.ppp_mpa
            this.pnp_mpa = this.ppp_mpa
            this.derated_ppp_mpa_unformatted = this.ppp_mpa * this.d_reas_coeff * (this.isManualBrainPressureTransmission ? this.brain_pressure_transmission_manual : this.brain_pressure_transmission)
            this.derated_ppp_mpa = +(this.derated_ppp_mpa_unformatted.toFixed(4))
            this.derated_pnp_mpa = +(this.derated_ppp_mpa_unformatted.toFixed(4))
            this.getIsppa(this.ppp_mpa, this.derated_ppp_mpa_unformatted, 'isppa', 'PR')
        },
        updateByIsppa() {
            const newCoeff = -(
              Math.log(this.brain_pressure_transmission) * 8.686
            ) / (this.distance_in_brain_cm * this.frequency_MHz);
            this.coeff_dB_cm_MHz = +newCoeff.toFixed(3);

            if (!this.primary_parameters_valid && !this.time_parameters_valid) return
            if (this.isppa > 0 && this.isppa !== "") this.getPampl()
        },
        updateByMI() {
            if (!this.primary_parameters_valid && !this.time_parameters_valid) return
            const attenuation_coefficient = 0.3;
            const attenuation_factor = attenuation_coefficient * this.depth_cm * this.frequency_MHz;
            // Calculate the peak rarefactional pressure (pnp_mpa) from the MI
            const peak_rarefractional_pressure = this.MI * Math.sqrt(this.frequency_MHz);

            // Apply the attenuation factor to calculate the original pnp_mpa
            this.ppp_mpa_unformatted = peak_rarefractional_pressure / Math.exp(-attenuation_factor / 8.686);
            this.ppp_mpa = +this.ppp_mpa_unformatted.toFixed(4)
            this.pnp_mpa = +this.ppp_mpa_unformatted.toFixed(4)
            this.derated_MI = this.MI * this.d_reas_coeff * this.brain_pressure_transmission
            this.derated_ppp_mpa_unformatted = this.derated_MI * Math.sqrt(this.frequency_MHz)
            this.derated_ppp_mpa = +(this.derated_ppp_mpa_unformatted.toFixed(4))
            this.derated_pnp_mpa = +(this.derated_ppp_mpa_unformatted.toFixed(4))
            this.getIsppa(this.ppp_mpa_unformatted, this.derated_ppp_mpa_unformatted, 'isppa','MI')
        },
        updateByNonDeratedMI() {
            if (!this.primary_parameters_valid && !this.time_parameters_valid) return

            this.ppp_mpa_unformatted = this.non_derated_MI * Math.sqrt(this.frequency_MHz);

            this.ppp_mpa = this.round(this.ppp_mpa_unformatted)
            this.pnp_mpa = this.round(this.ppp_mpa_unformatted)
            // this.calculateMI('non_derated_MI')
            this.derated_ppp_mpa_unformatted = this.derated_MI * Math.sqrt(this.frequency_MHz)
            this.derated_ppp_mpa = this.derated_ppp_mpa_unformatted
            this.derated_pnp_mpa = this.derated_ppp_mpa_unformatted
            this.getIsppa(this.ppp_mpa, this.derated_ppp_mpa, 'isppa', 'non_derated_MI')
        },
        updateByElectricalPowerOrTransducerEfficiency() {
            this.acoustic_power = +((this.transducer_efficiency / 100) * this.electrical_power).toFixed(2)
            this.getAverageAcousticPower()
        },
        updateByAcousticPower() {
            this.electrical_power = +(this.acoustic_power / (this.transducer_efficiency / 100)).toFixed(2)
            this.getAverageAcousticPower()
        }
  }
})